import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DisplayPrecision } from '../types/permissions';
import { useApp } from './AppContext';
import { getCurrentUser, getAllUsers } from '../api/services/userService';
import type { ChartHealthDisplay } from './UserPreferenceDefaultsContext';

export interface PersonaPreferences {
  // Display Precision
  displayPrecision: DisplayPrecision | null;

  // Forecast Alerts
  lowCashBalanceThreshold: number | null;

  // Transaction Alerts
  overdueInvoiceAlert: boolean | null;

  // Variance Alerts
  varianceAlertThreshold: number | null;

  // Notifications
  emailNotifications: boolean | null;
  feedNotifications: boolean | null;

  // Forecasting
  defaultForecastView: '7-day' | '30-day' | '90-day' | null;
  confidenceLevel: 'low' | 'medium' | 'high' | null;
  includeWeekends: boolean | null;

  // Display
  theme: 'light' | 'dark' | null;
  defaultLandingPage: string | null;

  // Avatar
  avatarUrl: string | null;

  // Health zone band boundaries (user overrides — null = use admin default)
  healthyThreshold: number | null;
  cautionThreshold: number | null;

  // Per-chart health zone display mode (user overrides — null = use admin default)
  chartHealthDashboard90: ChartHealthDisplay | null;
  chartHealthForecast90: ChartHealthDisplay | null;
  chartHealthForecast12m: ChartHealthDisplay | null;
  chartHealthForecast3y: ChartHealthDisplay | null;
}

interface UserPreferences {
  // Keyed by user ID (string) — works for both demo user IDs and live UUIDs
  preferences: Record<string, PersonaPreferences>;
}

interface UserPreferencesContextType {
  getUserPrecisionOverride: (userId: string) => DisplayPrecision | null;
  setUserPrecisionOverride: (userId: string, precision: DisplayPrecision | null) => void;
  getUserPreferences: (userId: string) => PersonaPreferences;
  updateUserPreferences: (userId: string, updates: Partial<PersonaPreferences>) => void;
  getAvatarUrlByName: (name: string) => string | null;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

const defaultUserPreferences: PersonaPreferences = {
  displayPrecision: null,
  lowCashBalanceThreshold: null,
  overdueInvoiceAlert: null,
  varianceAlertThreshold: null,
  emailNotifications: null,
  feedNotifications: null,
  defaultForecastView: null,
  confidenceLevel: null,
  includeWeekends: null,
  theme: null,
  defaultLandingPage: null,
  avatarUrl: null,
  healthyThreshold: null,
  cautionThreshold: null,
  chartHealthDashboard90: null,
  chartHealthForecast90: null,
  chartHealthForecast12m: null,
  chartHealthForecast3y: null,
};

const defaultPreferences: UserPreferences = {
  preferences: {},
};

function mapApiPreferencesToUser(apiPrefs: Record<string, string>): Partial<PersonaPreferences> {
  const mapped: Partial<PersonaPreferences> = {};

  if (apiPrefs.lowCashBalanceThreshold !== undefined) {
    mapped.lowCashBalanceThreshold = parseFloat(apiPrefs.lowCashBalanceThreshold);
  }
  if (apiPrefs.overdueInvoiceAlert !== undefined) {
    mapped.overdueInvoiceAlert = apiPrefs.overdueInvoiceAlert === 'true';
  }
  if (apiPrefs.varianceAlertThreshold !== undefined) {
    mapped.varianceAlertThreshold = parseFloat(apiPrefs.varianceAlertThreshold);
  }
  if (apiPrefs.emailNotifications !== undefined) {
    mapped.emailNotifications = apiPrefs.emailNotifications === 'true';
  }
  if (apiPrefs.feedNotifications !== undefined) {
    mapped.feedNotifications = apiPrefs.feedNotifications === 'true';
  }
  if (apiPrefs.defaultForecastView !== undefined) {
    const raw = apiPrefs.defaultForecastView;
    if (raw === '7-day' || raw === '30-day' || raw === '90-day') {
      mapped.defaultForecastView = raw;
    } else {
      mapped.defaultForecastView = null;
    }
  }
  if (apiPrefs.confidenceLevel !== undefined) {
    mapped.confidenceLevel = apiPrefs.confidenceLevel as PersonaPreferences['confidenceLevel'];
  }
  if (apiPrefs.includeWeekends !== undefined) {
    mapped.includeWeekends = apiPrefs.includeWeekends === 'true';
  }
  if (apiPrefs.theme !== undefined) {
    mapped.theme = apiPrefs.theme as PersonaPreferences['theme'];
  }
  if (apiPrefs.displayPrecision !== undefined) {
    mapped.displayPrecision = apiPrefs.displayPrecision as DisplayPrecision;
  }
  if (apiPrefs.defaultLandingPage !== undefined) {
    mapped.defaultLandingPage = apiPrefs.defaultLandingPage;
  }

  return mapped;
}

export const UserPreferencesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { mode, currentUser } = useApp();
  const isDemo = mode === 'demo';

  const { data: userData } = useQuery({
    queryKey: ['currentUser'],
    queryFn: getCurrentUser,
    enabled: mode === 'live',
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);

  // Reset preferences when switching to DEMO mode
  useEffect(() => {
    if (isDemo) {
      setPreferences(defaultPreferences);
    }
  }, [isDemo]);

  // Fetch all users in live mode to build avatar lookup map
  const { data: allUsersData } = useQuery({
    queryKey: ['allUsersAvatars'],
    queryFn: getAllUsers,
    enabled: mode === 'live',
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const [userAvatarMap, setUserAvatarMap] = useState<Record<string, string>>({});

  // Build avatar map from all users response
  useEffect(() => {
    if (allUsersData?.users) {
      const map: Record<string, string> = {};
      for (const user of allUsersData.users) {
        if (user.avatarUrl) {
          map[user.name] = user.avatarUrl;
        }
      }
      setUserAvatarMap(map);
    }
  }, [allUsersData]);

  // LIVE MODE: Load preferences from API response
  useEffect(() => {
    if (!isDemo && userData) {
      const mapped: Partial<PersonaPreferences> = {};

      if (userData.preferences && Object.keys(userData.preferences).length > 0) {
        Object.assign(mapped, mapApiPreferencesToUser(userData.preferences));
      }

      if (userData.avatarUrl) {
        mapped.avatarUrl = userData.avatarUrl;
      }

      if (Object.keys(mapped).length > 0) {
        setPreferences(prev => ({
          ...prev,
          preferences: {
            ...prev.preferences,
            [currentUser.id]: {
              ...(prev.preferences[currentUser.id] ?? defaultUserPreferences),
              ...mapped,
            },
          },
        }));
      }
    }
  }, [isDemo, userData, currentUser.id]);

  const getUserPrecisionOverride = (userId: string): DisplayPrecision | null => {
    return preferences.preferences[userId]?.displayPrecision || null;
  };

  const setUserPrecisionOverride = (userId: string, precision: DisplayPrecision | null) => {
    setPreferences(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [userId]: {
          ...(prev.preferences[userId] ?? defaultUserPreferences),
          displayPrecision: precision,
        },
      },
    }));
  };

  const getUserPreferences = (userId: string): PersonaPreferences => {
    return preferences.preferences[userId] ?? defaultUserPreferences;
  };

  const updateUserPreferences = (userId: string, updates: Partial<PersonaPreferences>) => {
    setPreferences(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [userId]: {
          ...(prev.preferences[userId] ?? defaultUserPreferences),
          ...updates,
        },
      },
    }));
  };

  // Lookup avatar URL by user name — works in both demo and live modes
  const getAvatarUrlByName = (name: string): string | null => {
    // Live mode: lookup from user avatar map (populated from GET /users)
    if (!isDemo && userAvatarMap[name]) {
      return userAvatarMap[name];
    }
    // Demo mode or live fallback: scan preferences by looking for an avatarUrl
    // match stored against a user ID — not needed for most cases, so return null
    return null;
  };

  return (
    <UserPreferencesContext.Provider
      value={{
        getUserPrecisionOverride,
        setUserPrecisionOverride,
        getUserPreferences,
        updateUserPreferences,
        getAvatarUrlByName,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};
